import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ButtonV2 } from '@withjoy/joykit';
import type { ButtonV2Props } from '@withjoy/joykit';
import { PeopleContext } from '@shared/context/people';
import { usePeoplePickerButtonController } from './usePeoplePickerButtonController';
import type { HardcodedLabel } from './usePeoplePickerButtonController';
import { PeopleListItem } from '@shared/components/PeopleListItem';
import { useTranslation } from '@shared/core';
import type { PeoplePickerUseCase } from './PeoplePicker.types';
import i18n from '@shared/core/i18n/i18n';

export type PeoplePickerItemType = Readonly<'simple' | 'withDeliveryMethod'>;

type PeoplePickerButtonProps = Readonly<
  Omit<ButtonV2Props, 'onClick' | 'children' | 'title' | 'itemType'> & {
    buttonTextDefault?: string;
    buttonTextHover?: string;
    eventHandle: string;
    title?: React.ReactNode;
    hardcodedLabels?: ReadonlyArray<HardcodedLabel>;
    onStartSelection?: () => void;
    useCase: PeoplePickerUseCase;
  } & ({ itemType?: 'simple'; enableSmsSelection?: never } | { itemType: 'withDeliveryMethod'; enableSmsSelection: boolean })
>;

export const PeoplePickerButton: React.FC<PeoplePickerButtonProps> = ({
  eventHandle,
  title: _title,
  hardcodedLabels,
  enableSmsSelection = false,
  itemType = 'simple',
  onStartSelection,
  useCase,

  buttonTextDefault: _buttonTextDefault,
  buttonTextHover,

  intent = 'primary',
  variant = 'outline',
  disabled,
  ...buttonProps
}) => {
  const { tq } = useTranslation('peoplePickerButton');

  useEffect(() => {
    // preload translation namespaces to avoid flickering
    i18n.loadNamespaces(['peoplePicker']);
  }, []);

  const { selectedPeople, setSelectedPeople } = useContext(PeopleContext);

  const title = _title || tq`Select Guests`;
  const buttonTextDefault = _buttonTextDefault || tq`Select Guests`;

  const [isHovering, setIsHovering] = useState(false);

  const personItemRender = useCallback(
    (person, handler) => {
      if (itemType === 'withDeliveryMethod') {
        return (
          <PeopleListItem.WithDeliveryMethod
            enableSmsSelection={enableSmsSelection}
            onDeliveryMethodChange={handler}
            {...person}
          />
        );
      }

      return <PeopleListItem.Simple {...person} />;
    },
    [itemType, enableSmsSelection],
  );

  const { loading, startSelection, error } = usePeoplePickerButtonController({
    eventHandle,
    title,
    onSelectedPeople: setSelectedPeople,
    enableSmsSelection,
    hardcodedLabels,
    personItemRender,
    useCase,
  });

  const handleClick = () => {
    onStartSelection?.();
    startSelection();
  };

  const buttonText = useMemo(
    () => (isHovering && selectedPeople?.length && buttonTextHover ? buttonTextHover : buttonTextDefault),
    [isHovering, buttonTextHover, buttonTextDefault, selectedPeople],
  );

  return (
    <ButtonV2
      {...buttonProps}
      intent={intent}
      variant={variant}
      disabled={disabled || !!error}
      loading={loading}
      onClick={handleClick}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      {buttonText}
    </ButtonV2>
  );
};
